import type { Editor } from '@tiptap/core';
import type { Node as PMNode } from '@tiptap/pm/model';
import type { Transaction } from '@tiptap/pm/state';
import { useEffect, useState } from 'react';

interface UseSiblingNodeInfoProps {
    /**
     * The TipTap editor instance
     */
    editor: Editor;
    /**
     * Function to get the current position of the node
     */
    getPos: () => number | undefined;
    /**
     * The name of the node type to count among siblings (e.g., 'zyProblem')
     */
    nodeTypeName: string;
}

interface SiblingNodeInfo {
    /**
     * The zero-based index of this node among its siblings of the same type.
     * Returns -1 if the position cannot be determined.
     */
    nodeIndex: number;
    /**
     * The total count of sibling nodes of the same type in the parent.
     */
    totalNodes: number;
    /**
     * The nodes of the same type as the node to count among siblings
     */
    nodesOfType: PMNode[];
    /**
     * The parent node of the node to count among siblings
     */
    parent: PMNode | null;
}

function getSiblingInfo(editor: Editor, getPos: () => number | undefined, nodeTypeName: string): SiblingNodeInfo {
    if (!getPos || typeof getPos !== 'function') {
        return { nodeIndex: -1, totalNodes: 0, parent: null, nodesOfType: [] };
    }

    const pos = getPos();
    if (pos === undefined) {
        return { nodeIndex: -1, totalNodes: 0, parent: null, nodesOfType: [] };
    }

    if (editor.state.doc.content.size < pos) {
        return { nodeIndex: -1, totalNodes: 0, parent: null, nodesOfType: [] };
    }

    const $pos = editor.state.doc.resolve(pos);
    const parent = $pos.parent;

    let nodeIndex = 0;
    let totalNodes = 0;
    let found = false;
    const nodesOfType: PMNode[] = [];

    parent.forEach((child, _offset, childIndex) => {
        if (child.type.name === nodeTypeName) {
            if ($pos.index($pos.depth) === childIndex) {
                found = true;
                nodeIndex = totalNodes;
            }
            nodesOfType.push(child);
            totalNodes++;
        }
    });

    return {
        nodeIndex: found ? nodeIndex : -1,
        totalNodes,
        nodesOfType,
        parent,
    };
}

function siblingInfoEqual(a: SiblingNodeInfo, b: SiblingNodeInfo): boolean {
    return (
        a.nodeIndex === b.nodeIndex &&
        a.totalNodes === b.totalNodes &&
        a.parent === b.parent &&
        a.nodesOfType.length === b.nodesOfType.length &&
        a.nodesOfType.every((node, i) => node === b.nodesOfType[i])
    );
}

/**
 * Hook to track a node's position and count among its siblings of the same type.
 * Automatically updates when siblings are added, removed, or reordered.
 *
 * Useful for node views that need to display their index (e.g., "Problem 2 of 5")
 * or conditionally render UI based on sibling count (e.g., hide delete button when only one exists).
 *
 */
export function useSiblingNodeInfo({ editor, getPos, nodeTypeName }: UseSiblingNodeInfoProps): SiblingNodeInfo {
    const [siblingInfo, setSiblingInfo] = useState<SiblingNodeInfo>(() => getSiblingInfo(editor, getPos, nodeTypeName));

    useEffect(() => {
        const handleUpdate = ({ transaction }: { transaction: Transaction }) => {
            if (!transaction.docChanged) {
                return;
            }
            setSiblingInfo(prev => {
                const next = getSiblingInfo(editor, getPos, nodeTypeName);
                return siblingInfoEqual(prev, next) ? prev : next;
            });
        };

        editor.on('transaction', handleUpdate);
        return () => {
            editor.off('transaction', handleUpdate);
        };
    }, [editor, getPos, nodeTypeName]);

    return siblingInfo;
}
